import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@material-ui/core';
import { getPaidNewsletterCreatorStore, PublishPaidNewsletterStart } from '@/store/paidNewsletterCreator/actions';
import { TertiaryButton, WebsitePrimaryButton } from '@dev/ee-components';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { BillingPaths, pathsParams } from '@/services/dictionaries/Paths';
import { NewsletterStatusEnum, ProductCode } from '@dev/ee-api/ee-api-models';
import { LockIcon } from '@/services/icons/LockedIcon';
import { getPaidNewslettersStripeAccountStore } from '@/store/paidNewslettersStripeAccount/actions';
import { usePublishModalParams } from '@/components/PaidNewsletterCreator/hooks/usePublishModalParams';
import { useHistoryBackActionContext } from '@/services/hooks/useHistoryBackAction';
import { HideDialog, ShowDialog } from '@/store/dialog/actions';
import { PaidNewsletterCreatorStatus } from '@/store/paidNewsletterCreator/types';
import { sendEventToGTM } from '@dev/ee-tools';
import { useSecurity } from '@/services/hooks/useSecurity';

interface FooterContentProps {
    onExit: () => void;
    isReady: boolean;
    isEditAvailable: boolean;
}

export const FooterContent = ({ onExit, isReady, isEditAvailable }: FooterContentProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { paidNewsletterObject, stepInEdit, status } = useSelector(getPaidNewsletterCreatorStore);
    const { isPilotPlan, isStandardPlan, productCode } = useCurrentPlanStatus();
    const { hasLegacyPlan, isMarketingPlan } = useSecurity();
    const { paidNewslettersStripeAccount, isLoading, isLoaded, isError } = useSelector(getPaidNewslettersStripeAccountStore);
    const historyBackAction = useHistoryBackActionContext();
    const { pushBackCallback, popBackCallback } = historyBackAction || {};

    const { getPublishModalProps } = usePublishModalParams({ popBackCallback });

    const isSaving = status === PaidNewsletterCreatorStatus.SAVING;
    const isPublishDisabled = !isEditAvailable || isLoading || stepInEdit > 0 || !isReady || isSaving;
    const canPublishNewsletter = !(isPilotPlan || (!hasLegacyPlan && isMarketingPlan && isStandardPlan));
    const isConnectInitiated = !isError && isLoaded && (!paidNewslettersStripeAccount?.TosAcceptance || !paidNewslettersStripeAccount?.ChargesEnabled);
    const isConnectFinished = !isError && isLoaded && paidNewslettersStripeAccount?.TosAcceptance && paidNewslettersStripeAccount?.ChargesEnabled;

    const onUpgrade = (): void => {
        window.location.href = `${location.origin}${BillingPaths.BillingPlanList.replace(
            pathsParams.productCode,
            (productCode === ProductCode.CreatorSuite ? ProductCode.CreatorSuite : ProductCode.Marketing).toLowerCase()
        )}`;
    };

    useEffect((): void => {
        if (!isPublishDisabled){
            sendEventToGTM("complete_paid_newsletter");
        }
    }, [isPublishDisabled])

    const closeModal = (): boolean => {
        dispatch(HideDialog());
        return true;
    }; 

    const onPublish = (): void => {
        pushBackCallback(closeModal);
        if (isConnectFinished) {
            dispatch(PublishPaidNewsletterStart());
            return;
        }
        dispatch(ShowDialog(getPublishModalProps(isConnectInitiated)));
    };

    if (paidNewsletterObject?.Status === NewsletterStatusEnum.Published) {
        return (<WebsitePrimaryButton text={t('backtopaidnewsletters')} onClick={onExit} />);
    }

    return (
        <Grid container alignItems="baseline" justifyContent="space-between" spacing={3}>
            <Grid item>
                {!canPublishNewsletter && <WebsitePrimaryButton
                    text={t('upgradetounlock')} 
                    onClick={onUpgrade} 
                    startIcon={<LockIcon width={20} height={20} />}
                    disabled={isPublishDisabled}
                />}
                {canPublishNewsletter && <WebsitePrimaryButton
                    text={t('publish')} 
                    onClick={onPublish} 
                    disabled={isPublishDisabled}
                    isLoading={isSaving}
                />}
            </Grid>
            <Grid item><TertiaryButton text={t('backtopaidnewsletters')} onClick={onExit} /></Grid>
        </Grid>
    );
};
